import React, { useState, useEffect, useRef } from "react";
import useAxiosPrivate from '../../hooks/useAxiosPrivate'
import { useLocation, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { BsThreeDotsVertical } from "react-icons/bs";
import RecommendationWrapper from "./component/Dashboard/RecommendationWrapper";
import Fuse from 'fuse.js';
import { DeleteModal } from "../Table/component/Table";

const VendorCard = ({ vendor, onEdit, onDelete }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null)

  // Close menu if clicked outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const vendorLabelColor = (label) => {
    const colors = {
        "Carbon Neutral":
          "text-xs px-2 py-1 rounded bg-green-100 text-green-800 dark:bg-green-300 dark:text-green-900",
        "Optimally Carbon Neutral":
          "text-xs px-2 py-1 rounded bg-orange-100 text-orange-800 dark:bg-orange-300 dark:text-orange-900",
      };

      return colors[label] || "text-xs px-2 py-1 rounded bg-gray-100 text-gray-800";
  }

  return (
    <div className="relative border bg-white shadow-lg dark:bg-darkbg4 rounded-xl shadow-sm mb-6 py-6 px-8 bg-white">
      <div className="absolute right-3" ref={menuRef}>
        <button
          onClick={() => setMenuOpen((prev) => !prev)}
          className="rounded-full p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <BsThreeDotsVertical className="h-4 w-4 text-gray-600 dark:text-white" />
        </button>

        {/* Dropdown menu */}
        {menuOpen && (
          <div className="absolute right-0 mt-1 w-28 bg-white border rounded-md shadow-lg z-20">
            <button
              onClick={() => {
                setMenuOpen(false);
                onEdit(vendor);
              }}
              className="block w-full rounded-t-md text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              Edit
            </button>
            <button
              onClick={() => {
                setMenuOpen(false);
                onDelete(vendor.id);
              }}
              className="block w-full rounded-b-md text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
            >
              Delete
            </button>
          </div>
        )}

      </div>

      <div className="flex items-start gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold">{vendor.name}</h2>
            <span className={vendorLabelColor(vendor.label)}>
              {vendor.label || "Pending"}
            </span>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{vendor.industry}</p>
          <p className="text-sm mt-2">{vendor.description}</p>
          {vendor.commitment && (
            <>
              <h3 className="text-sm font-semibold mt-3">Company's Commitment</h3>
              <p className="text-sm">{vendor.commitment}</p>
            </>
          )}
          {showDetails && vendor.details && (
            <div className="mt-3 text-sm">
              <p>
                <strong>Maturity:</strong> {vendor.details.maturity} <br />
                <strong>Focus area:</strong> {vendor.details.focus} <br />
                <strong>Location:</strong> {vendor.details.location} <br />
                <strong>Latest review:</strong> {new Date(vendor.updatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            </div>
          )}
          <div className="mt-4 flex gap-2">
            <a
              href={vendor.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Source
            </a>
            <button
              className="text-blue-600 dark:text-blue-400 underline text-sm"
              onClick={() => setShowDetails(!showDetails)}
            >
              {showDetails ? "Show less" : "More"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Vendors = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const axiosPrivate = useAxiosPrivate()
  const [vendors, setVendors] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([])
  const [showContentModal, setShowContentModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [modalMode, setModalMode] = useState(null) // 'add' or 'edit'
  const [rowToDelete, setRowToDelete] = useState(null)
  const [editableData, setEditableData] = useState({
    name: "",
    industry: "",
    description: "",
    commitment: "",
    maturity: "",
    focus: "",
    location: "",
    sourceUrl: ""
  })
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchVendors = async () => {
      setIsLoading(true);
      try {
        const response = await axiosPrivate.get('vendors/read-record', {
          signal: controller.signal,
        });

        if (isMounted) {
          setVendors(response.data); // <-- define setCompanies state in your component
          setSearchResults(response.data)
        }
      } catch (err) {
        if (axios.isCancel(err)) {
          // Request cancelled, safe to ignore
        } else {
          console.error('Failed to fetch companies:', err);
          if (err.response && err.response.status === 403) {
            navigate('/sign-in', { state: { from: location }, replace: true });
          }
        }
      }
      setIsLoading(false);
    };

    fetchVendors();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [navigate, location, axiosPrivate]);

  // Filter data based on search term
  useEffect(() => {
    // Initialize Fuse with the list of users and the options for fuzzy search
    const fuse = new Fuse(vendors, {
      keys: ['name', 'description', 'focus'],
      includeScore: true,
    });

    if (searchTerm.trim().length > 0) {
      const results = fuse.search(searchTerm).map((result) => result.item);
      setSearchResults(results);
    } else {
      setSearchResults(vendors); // Reset to original data if search term is cleared
    }
  }, [searchTerm, vendors]);

  const handleAdd = () => {
    setEditableData({
      name: "",
      industry: "",
      description: "",
      commitment: "",
      maturity: "",
      focus: "",
      location: "",
      sourceUrl: ""
    })
    setModalMode('add')
    setShowContentModal(true)
  };
  const handleEdit = (group) => {
    setEditableData({
      ...group,        // spread top-level fields
      ...group.details // spread details into top-level
    })
    setModalMode('edit')
    setShowContentModal(true)
  };

  const handleDelete = (id) => {
    setShowDeleteModal(true)
    setRowToDelete(id)
  };

  const confirmDelete = async () => {
    if (rowToDelete !== null) {
      let isMounted = true
      const controller = new AbortController()

      try {
        await axiosPrivate.delete(`/vendors/delete-record/${rowToDelete}`, {
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
        })

        if (isMounted) {
          // Update the state to reflect the deletion
          setVendors((prevData) =>
            prevData.filter((row) => row.id !== rowToDelete),
          )
          setSearchResults((prevData) =>
            prevData.filter((row) => row.id !== rowToDelete),
          )
          setRowToDelete(null)
        }
      } catch (err) {
        if (!axios.isCancel(err)) {
          console.error('Deletion failed:', err)
          if (err.response) {
            // Redirect on 403 Forbidden or show an error message
            if (err.response.status === 403) {
              navigate('/sign-in', {
                state: { from: location },
                replace: true,
              })
            } else {
              alert(
                `Error: ${err.response.data.message || 'An error occurred.'}`,
              )
            }
          }
        }
      } finally {
        if (isMounted) {
          setShowDeleteModal(false) // Close the modal upon completion or error
        }
      }

      // Cleanup function to prevent state updates if the component unmounts
      return () => {
        isMounted = false
        controller.abort()
      }
    } else {
      // If rowToDelete is null, just close the modal
      setShowDeleteModal(false)
    }
  }

  const handleAddNewSubmit = async (event) => {
    event.preventDefault()

    const endpoint =
      modalMode === 'edit'
        ? `/vendors/edit-record/${editableData.id}`
        : '/vendors/create-record'
    const method = modalMode === 'edit' ? 'patch' : 'post'
    // Prepare the data payload
    const payload = {
        name: editableData.name,
        industry: editableData.industry,
        description: editableData.description,
        commitment: editableData.commitment,
        maturity: editableData.maturity,
        focus: editableData.focus,
        location: editableData.location,
        sourceUrl: editableData.sourceUrl
    }

    try {
      const response = await axiosPrivate[method](endpoint, payload, {
        headers: { 'Content-Type': 'application/json' },
      })

      // Update the state based on the modal mode
      if (modalMode === 'edit') {
        setVendors((currentData) =>
          currentData.map((item) =>
            item.id === editableData.id ? { ...item, ...response.data } : item,
          ),
        )
      } else {
        setVendors((currentData) => [...currentData, response.data])
      }

      // Reset form and close modal
      setEditableData({
        id: "",
        name: "",
        industry: "",
        description: "",
        commitment: "",
        maturity: "",
        focus: "",
        location: "",
        sourceUrl: ""
      })
      setShowContentModal(false)
    } catch (err) {
      if (!axios.isCancel(err)) {
        console.error('Submission failed:', err)
        const errorMsg = err.response?.data.message || 'An error occurred.'
        if (err.response?.status === 403) {
          navigate('/sign-in', { state: { from: location }, replace: true })
        } else {
          alert(`Error: ${errorMsg}`)
        }
      }
    }
  }

  return (
      <RecommendationWrapper
        title="Recommended Vendors"
        onAdd={handleAdd}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        placeholder="Search ">
        {searchResults.map((vendor) => (
          <VendorCard
            key={vendor.id}
            vendor={vendor}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        ))}


      {showDeleteModal && (
        <DeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={(row) => (row !== null ? confirmDelete() : setShowDeleteModal(false))}
        rowToDelete={rowToDelete}
      />
      )}

    {showContentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-md flex justify-center items-center p-4">
          <div className="bg-white dark:bg-gray-800 w-full max-w-lg mx-auto rounded shadow-lg overflow-hidden p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4">
              {modalMode === 'edit' ? 'Edit Data' : 'Add New Data'}
            </h2>
            <form onSubmit={handleAddNewSubmit}>
              {/* Title Input */}
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Name
                </label>
                <input
                  type="text"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  value={editableData.name}
                  onChange={(e) =>
                    setEditableData({
                      ...editableData,
                      name: e.target.value,
                    })
                  }
                  required
                />
              </div>

              {/* Tags Input */}
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Industry
                </label>
                <input
                  type="text"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  value={editableData.industry}
                  onChange={(e) =>
                    setEditableData({
                      ...editableData,
                      industry: e.target.value,
                    })
                  }
                  required
                />
              </div>

              {/* Description Input */}
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Description
                </label>
                <input
                  type="text"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  value={editableData.description}
                  onChange={(e) =>
                    setEditableData({
                      ...editableData,
                      description: e.target.value,
                    })
                  }
                  required
                />
              </div>

              {/* Commitment Input */}
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Commitment
                </label>
                <input
                  type="text"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  value={editableData.commitment}
                  onChange={(e) =>
                    setEditableData({
                      ...editableData,
                      commitment: e.target.value,
                    })
                  }
                  required
                />
              </div>

              {/* Maturity Input */}
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Maturity
                </label>
                <select
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  value={editableData.maturity}
                  onChange={(e) =>
                    setEditableData({
                      ...editableData,
                      maturity: e.target.value,
                    })
                  }
                  required
                >
                  <option value="">Select Level</option>
                  <option value="Established">Established</option>
                </select>
              </div>

              {/* Focus Input */}
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Focus
                </label>
                <input
                  type="text"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  value={editableData.focus}
                  onChange={(e) =>
                    setEditableData({
                      ...editableData,
                      focus: e.target.value,
                    })
                  }
                  required
                />
              </div>

              {/* Location Input */}
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Location
                </label>
                <input
                  type="text"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  value={editableData.location}
                  onChange={(e) =>
                    setEditableData({
                      ...editableData,
                      location: e.target.value,
                    })
                  }
                  required
                />
              </div>

              {/* University Input */}
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Source URL
                </label>
                <input
                  type="text"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  value={editableData.sourceUrl}
                  onChange={(e) =>
                    setEditableData({
                      ...editableData,
                      sourceUrl: e.target.value,
                    })
                  }
                  required
                />
              </div>

              <div className="flex justify-end mt-4">
                <button
                  type="button"
                  onClick={() => setShowContentModal(false)}
                  className="bg-gray-300 dark:bg-gray-600 dark:text-gray-200 px-4 py-2 rounded mr-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-500 text-white px-4 py-2 rounded"
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </RecommendationWrapper>

  );
};

export default Vendors;